// src/components/SurveyDetail.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../api';

/* Einfache Balken-Zeile (Label links, Anzahl+% rechts, darunter Progress-Bar) */
function BarRow({ label, count, total, color='#06A69E' }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{marginBottom:8}}>
      <div style={{display:'flex', justifyContent:'space-between', fontSize:14}}>
        <strong style={{marginRight:8, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{label}</strong>
        <span>{count} ({pct}%)</span>
      </div>
      <div style={{height:10, background:'#eee', borderRadius:999, overflow:'hidden'}}>
        <div style={{width:`${pct}%`, height:'100%', background:color}} />
      </div>
    </div>
  );
}

/* Prüfen, ob ein Count-Set de facto yes/no ist */
function isYesNoCounts(counts) {
  const keys = Object.keys(counts || {}).map(k => k.toLowerCase()).sort();
  if (keys.length === 0) return false;
  const onlyYes  = keys.length === 1 && keys[0] === 'yes';
  const onlyNo   = keys.length === 1 && keys[0] === 'no';
  const yesNo    = keys.length === 2 && keys[0] === 'no' && keys[1] === 'yes';
  return onlyYes || onlyNo || yesNo;
}

function DynamicBars({ counts }) {
  const entries = Object.entries(counts || {});
  const total = entries.reduce((s, [,c]) => s + (c||0), 0);

  // sort: desc by count
  entries.sort((a,b) => (b[1]||0) - (a[1]||0));

  // map label deutsch für yes/no
  const labelOf = (k) => {
    const v = String(k).toLowerCase();
    if (v === 'yes') return 'Ja';
    if (v === 'no')  return 'Nein';
    return k;
  };

  return (
    <div>
      {entries.length === 0 ? <div style={{opacity:.7}}>Keine Antworten</div> : (
        entries.map(([k,c]) => (
          <BarRow key={k} label={labelOf(k)} count={c||0} total={total} />
        ))
      )}
    </div>
  );
}

/* Freitext hübsch gruppieren: Top-Antworten mit Balken + Rohliste */
function TextAnswers({ answers }) {
  const grouped = useMemo(()=>{
    const map = new Map();
    (answers||[]).forEach(a=>{
      const v = (a?.value || '').trim();
      if (!v) return;
      map.set(v, (map.get(v)||0) + 1);
    });
    const arr = Array.from(map.entries());
    arr.sort((a,b)=> b[1]-a[1]); // by count desc
    return arr;
  }, [answers]);

  const total = grouped.reduce((s,[,c])=>s+c,0);

  if (grouped.length === 0) {
    return <div style={{opacity:.7}}>Keine Text-Antworten</div>;
  }

  // Zeige die Top 5 als kleine Balken
  const top = grouped.slice(0,5);

  return (
    <div>
      <div style={{marginBottom:10, fontWeight:700}}>Häufigste Antworten</div>
      {top.map(([txt,c])=>(
        <BarRow key={txt} label={txt} count={c} total={total} />
      ))}

      <details style={{marginTop:10}}>
        <summary>Alle Antworten anzeigen ({answers?.length || 0})</summary>
        <ul style={{marginTop:10}}>
          {(answers||[]).map((a,i)=>(
            <li key={i} style={{fontVariantNumeric:'tabular-nums'}}>
              <small>{a.created_at}</small> — <span>{a.value || <em>(leer)</em>}</span> <span style={{opacity:.7}}>(resp #{a.response_id})</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

export default function SurveyDetail({ slug, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(()=>{
    (async ()=>{
      try{
        setLoading(true); setErr('');
        const res = await apiFetch(`admin/survey_detail.php?slug=${encodeURIComponent(slug)}`);
        const text = await res.text();
        const js = JSON.parse(text);
        if (!js.ok) throw new Error(js.error || 'Fehler');
        setData(js);
      } catch(e) {
        setErr(e.message || 'Laden fehlgeschlagen');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Varianten-Buchstaben (a,b,c,…) je Frage/Variante
  const variantLabels = useMemo(()=>{
    const map = {};
    if (!data?.questions) return map;
    data.questions.forEach(q=>{
      const keys = q.matrix ? Object.keys(q.matrix) : [];
      keys.forEach((t, i)=>{ map[`${q.id}::${t}`] = String.fromCharCode(97+i); });
    });
    return map;
  }, [data]);

  return (
    <div>
      <button className="secondary" onClick={onBack} style={{ marginBottom: 12 }}>← Zurück</button>
      <h2>Umfrage: {slug}</h2>

      {err && <div className="card" style={{color:'crimson',borderColor:'crimson'}}>{err}</div>}
      {loading && <div className="card">Lade…</div>}

      {!loading && !err && data && (
        <>
          <div className="cards" style={{display:'flex', gap:'1rem', flexWrap:'wrap', marginBottom:'1rem'}}>
            <div className="card"><div>Teilnahmen</div><div style={{fontSize:'1.6rem',fontWeight:700}}>{data.summary?.responses ?? 0}</div></div>
            <div className="card"><div>Erste</div><div style={{fontSize:'1.1rem'}}>{data.summary?.first_at || '–'}</div></div>
            <div className="card"><div>Letzte</div><div style={{fontSize:'1.1rem'}}>{data.summary?.last_at || '–'}</div></div>
          </div>

          {(data.questions || []).map(q => {
            const counts = q.counts || {};
            const isYN   = isYesNoCounts(counts);
            const matrix = q.matrix || {};
            const variantTexts = Object.keys(matrix);
            const hasVariants = variantTexts.length > 1 || (variantTexts.length===1 && (variantTexts[0] !== q.text));

            return (
              <div key={q.id} className="card" style={{marginBottom:'1rem'}}>
                <h3 style={{marginTop:0}}>Frage {q.id}</h3>
                <div style={{fontWeight:600, marginBottom:10}}>{q.text}</div>

                {/* Gesamtauswertung */}
                {q.type === 'text'
                  ? <TextAnswers answers={q.answers || []} />
                  : <DynamicBars counts={counts} />
                }

                {/* Variantenaufteilung – falls vorhanden */}
                {q.type !== 'text' && hasVariants && (
                  <div style={{marginTop:14}}>
                    <div style={{fontWeight:700, marginBottom:8}}>Aufgeschlüsselt nach Variante</div>
                    {variantTexts.map((txt, idx) => {
                      const letter = variantLabels[`${q.id}::${txt}`] || String.fromCharCode(97+idx);
                      return (
                        <div key={txt} style={{padding:'10px 0', borderTop: idx===0 ? 'none' : '1px solid #eee'}}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:8}}>
                            <div style={{fontWeight:700}}>Frage {q.id}{letter}</div>
                            <div style={{fontSize:12, opacity:.75}}>Variante</div>
                          </div>
                          <div style={{fontSize:14, margin:'4px 0 8px'}}>{txt}</div>
                          <DynamicBars counts={matrix[txt] || {}} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rohantworten ein-/ausklappbar (auch bei text zusätzlich nützlich) */}
                {Array.isArray(q.answers) && q.answers.length > 0 && (
                  <details style={{marginTop:10}}>
                    <summary>Rohantworten anzeigen ({q.answers.length})</summary>
                    <ul style={{marginTop:10}}>
                      {q.answers.map((a,i)=>(
                        <li key={i} style={{fontVariantNumeric:'tabular-nums'}}>
                          <small>{a.created_at}</small> — <strong>{a.value || <em>(leer)</em>}</strong> <span style={{opacity:.7}}>(resp #{a.response_id})</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
